import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Download, FileText, Loader2, MessageSquare, Paperclip, Scale, ShieldCheck, X } from 'lucide-react';
import {
  dienKlachtIn,
  getKlachtContext,
  getMijnKlachten,
  KLACHT_CATEGORIEEN,
  KLACHT_GEBRUIKER_TYPES,
  type KlachtCategorie,
  type KlachtStatus,
} from '../../services/klachtApi';
import { useAuthStore } from '../../store/authStore';
import { KlachtenreglementModal } from './KlachtenreglementModal';
import { KLACHTENREGLEMENTEN, reglementVariantVoorRol } from './klachtenreglementen';

const KLACHT_CONTEXT_KEY = ['klachten', 'context'];
const MIJN_KLACHTEN_KEY = ['klachten', 'mijn'];

const MAX_BIJLAGE = 10 * 1024 * 1024;
const ANDERS = '__anders__';

const STATUS_KLEUR: Record<KlachtStatus, string> = {
  ontvangen: 'bg-amber-50 text-amber-700 border-amber-200',
  in_behandeling: 'bg-sky-50 text-sky-700 border-sky-200',
  afgehandeld: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export interface KlachtVoorinvulling {
  /** Wisselt bij elke nieuwe voorinvulling, zodat dezelfde boeking opnieuw gekozen kan worden. */
  sleutel: number;
  referentie?: string;
  categorie?: KlachtCategorie;
}

/** uiterste_reactiedatum komt als kalenderdatum (YYYY-MM-DD); die als lokale datum lezen, niet als UTC. */
const naarDatum = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T00:00:00`) : new Date(iso));
const datumLang = (iso: string, lng: string) =>
  naarDatum(iso).toLocaleDateString(lng, { day: 'numeric', month: 'long', year: 'numeric' });

const invoerCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-navy-900 focus:outline-none focus:ring-1 focus:ring-navy-900';
const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

/**
 * Klachtenreglement platform: PDF-download bovenaan, het formulier (5 secties)
 * en de eigen ingediende klachten. Ingebed op de vier vaste plekken:
 * opdrachtgever (Mijn Cockpit › Juridisch & Support), professional (Mijn Belagom ›
 * Contracten & Documenten), opleider (Finance › Helpdesk & Escalaties) en
 * medewerker (Academy › Helpdesk & transactiefouten).
 * Met `formulierAchterKnop` verschijnt het formulier pas na een klik op "Klacht indienen".
 */
export const KlachtenSectie: React.FC<{ voorinvulling?: KlachtVoorinvulling; toonKop?: boolean; formulierAchterKnop?: boolean }> = ({
  voorinvulling,
  toonKop = true,
  formulierAchterKnop = false,
}) => {
  const { t, i18n } = useTranslation('klachten');
  const queryClient = useQueryClient();
  // Medewerkers (Academy-kopers) krijgen de consumentenversie van het reglement, zakelijke rollen de B2B-versie
  const variant = reglementVariantVoorRol(useAuthStore((s) => s.user?.role));
  const reglement = KLACHTENREGLEMENTEN[variant];
  const consument = variant === 'consument';
  const [modalOpen, setModalOpen] = useState(false);
  const [formulierOpen, setFormulierOpen] = useState(!formulierAchterKnop);

  const context = useQuery({ queryKey: KLACHT_CONTEXT_KEY, queryFn: getKlachtContext, staleTime: 5 * 60 * 1000 });
  const mijn = useQuery({ queryKey: MIJN_KLACHTEN_KEY, queryFn: getMijnKlachten });

  const [contactNaam, setContactNaam] = useState('');
  const [email, setEmail] = useState('');
  const [telefoon, setTelefoon] = useState('');
  const [categorie, setCategorie] = useState<KlachtCategorie | ''>('');
  const [boekingKeuze, setBoekingKeuze] = useState('');
  const [referentie, setReferentie] = useState('');
  const [omschrijving, setOmschrijving] = useState('');
  const [oplossing, setOplossing] = useState('');
  const [bijlage, setBijlage] = useState<File | null>(null);
  const [akkoord, setAkkoord] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [ontvangen, setOntvangen] = useState<{ ticket: string; datum: string } | null>(null);
  const bestandRef = useRef<HTMLInputElement>(null);

  const ctx = context.data;
  const isMedewerker = ctx?.gebruiker_type === 'EMPLOYEE_CONSUMER';
  const boekingen = ctx?.boekingen ?? [];

  // Contactgegevens eenmalig voorinvullen zodra de context binnen is
  useEffect(() => {
    if (!ctx) return;
    setContactNaam((v) => v || ctx.contact_naam);
    setEmail((v) => v || ctx.email);
    setTelefoon((v) => v || ctx.telefoon);
  }, [ctx]);

  // Voorinvulling vanuit bv. "Probleem melden" bij een Academy-boeking
  useEffect(() => {
    if (!voorinvulling) return;
    setOntvangen(null);
    setFormulierOpen(true);
    if (voorinvulling.categorie) setCategorie(voorinvulling.categorie);
    if (voorinvulling.referentie) {
      setReferentie(voorinvulling.referentie);
      setBoekingKeuze(voorinvulling.referentie);
    }
  }, [voorinvulling]);

  const effectieveReferentie = isMedewerker && boekingKeuze && boekingKeuze !== ANDERS ? boekingKeuze : referentie;

  const indienen = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('contact_naam', contactNaam.trim());
      form.append('email', email.trim());
      form.append('telefoon', telefoon.trim());
      form.append('categorie', categorie);
      form.append('referentie', effectieveReferentie.trim());
      form.append('omschrijving', omschrijving.trim());
      form.append('gewenste_oplossing', oplossing.trim());
      form.append('akkoord', String(akkoord));
      if (bijlage) form.append('bijlage', bijlage);
      return dienKlachtIn(form);
    },
    onSuccess: (klacht) => {
      setOntvangen({ ticket: klacht.ticket_nummer, datum: datumLang(klacht.uiterste_reactiedatum, i18n.language) });
      setCategorie('');
      setBoekingKeuze('');
      setReferentie('');
      setOmschrijving('');
      setOplossing('');
      setBijlage(null);
      setAkkoord(false);
      queryClient.invalidateQueries({ queryKey: MIJN_KLACHTEN_KEY });
    },
    onError: (err: { response?: { data?: { message?: string } } }) => setFout(err?.response?.data?.message || t('fout.indienen')),
  });

  const kiesBijlage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const bestand = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!bestand) return;
    if (!['application/pdf', 'image/png'].includes(bestand.type)) return setFout(t('fout.bijlageType'));
    if (bestand.size > MAX_BIJLAGE) return setFout(t('fout.bijlageGrootte'));
    setFout(null);
    setBijlage(bestand);
  };

  const kanIndienen =
    contactNaam.trim().length >= 2 &&
    email.trim() &&
    telefoon.trim() &&
    categorie &&
    effectieveReferentie.trim() &&
    omschrijving.trim().length >= 20 &&
    oplossing.trim().length >= 5 &&
    akkoord &&
    !indienen.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setFout(null);
    if (kanIndienen) indienen.mutate();
  };

  return (
    <div className="space-y-6">
      {toonKop && (
        <section className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-navy-50">
                <Scale className="h-5 w-5 text-navy-900" aria-hidden="true" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">{t('kop.titel')}</h2>
                <p className="mt-1 max-w-2xl text-sm text-gray-600">{t('kop.intro')}</p>
              </div>
            </div>
            <div className="flex flex-shrink-0 flex-wrap gap-2">
              <a
                href={reglement.pdf}
                download
                className="inline-flex items-center gap-2 rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                {t('kop.pdf')}
              </a>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                {t('kop.lezen')}
              </button>
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-semibold">{t('let.titel')}</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              <li>{consument ? t('consument.termijn') : t('let.termijn')}</li>
              <li>{consument ? t('consument.wkkgz') : t('let.wkkgz')}</li>
              {consument && <li>{t('consument.rechter')}</li>}
              <li>{t('let.privacy')}</li>
            </ul>
          </div>
        </section>
      )}

      {context.isLoading ? (
        <div className="flex min-h-[200px] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : context.isError || !ctx ? (
        <p className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{t('fout.laden')}</p>
      ) : !formulierOpen ? (
        <section className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-600">{t('knop.openenIntro')}</p>
          <button
            type="button"
            onClick={() => setFormulierOpen(true)}
            className="inline-flex flex-shrink-0 items-center justify-center gap-2 rounded-xl bg-navy-900 px-6 py-2.5 text-sm font-semibold text-white hover:opacity-90"
          >
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
            {t('knop.openen')}
          </button>
        </section>
      ) : ontvangen ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" role="status">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" aria-hidden="true" />
            <div>
              <p className="font-semibold text-emerald-900">{t('succes.titel')}</p>
              <p className="mt-1 text-sm text-emerald-800">{t('succes.tekst', { ticket: ontvangen.ticket, datum: ontvangen.datum })}</p>
              <button type="button" onClick={() => setOntvangen(null)} className="mt-3 text-sm font-medium text-emerald-800 underline">
                {t('knop.nieuwe')}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <form id="klachtenformulier" onSubmit={submit} className="space-y-6 rounded-2xl border border-gray-200 bg-white p-5">
          {/* 1. Indiener */}
          <fieldset className="space-y-4">
            <legend className="mb-1 text-sm font-semibold uppercase tracking-wide text-navy-900">{t('sectie.indiener')}</legend>
            <div>
              <p className={labelCls}>{t('veld.type')}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {KLACHT_GEBRUIKER_TYPES.map((type) => {
                  const eigen = type === ctx.gebruiker_type;
                  return (
                    <label
                      key={type}
                      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${
                        eigen ? 'border-navy-900 bg-navy-50 text-gray-900' : 'border-gray-200 text-gray-400'
                      }`}
                    >
                      <input type="radio" name="gebruiker_type" checked={eigen} disabled={!eigen} readOnly className="mt-0.5" />
                      <span>{t(`type.${type}`)}</span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-1 text-xs text-gray-500">{t('veld.typeHint')}</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <p className={labelCls}>{isMedewerker ? t('veld.organisatieMedewerker') : t('veld.organisatie')}</p>
                <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {isMedewerker ? '—' : ctx.organisatie_naam || <span className="text-gray-400">{t('veld.organisatieOnbekend')}</span>}
                </p>
              </div>
              <div>
                <label htmlFor="klacht-contact" className={labelCls}>{t('veld.contactNaam')}</label>
                <input id="klacht-contact" className={invoerCls} value={contactNaam} onChange={(e) => setContactNaam(e.target.value)} placeholder={t('veld.contactNaamPlaceholder')} maxLength={200} required autoComplete="name" />
              </div>
              <div>
                <label htmlFor="klacht-email" className={labelCls}>{t('veld.email')}</label>
                <input id="klacht-email" type="email" className={invoerCls} value={email} onChange={(e) => setEmail(e.target.value)} maxLength={254} required autoComplete="email" />
              </div>
              <div>
                <label htmlFor="klacht-telefoon" className={labelCls}>{t('veld.telefoon')}</label>
                <input id="klacht-telefoon" type="tel" className={invoerCls} value={telefoon} onChange={(e) => setTelefoon(e.target.value)} placeholder={t('veld.telefoonPlaceholder')} maxLength={30} required autoComplete="tel" />
              </div>
            </div>
          </fieldset>

          {/* 2. Categorie */}
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-semibold uppercase tracking-wide text-navy-900">{t('sectie.categorie')}</legend>
            <label htmlFor="klacht-categorie" className={labelCls}>{t('veld.categorie')}</label>
            <select id="klacht-categorie" className={invoerCls} value={categorie} onChange={(e) => setCategorie(e.target.value as KlachtCategorie)} required>
              <option value="">{t('veld.categorieKies')}</option>
              {KLACHT_CATEGORIEEN.map((c, i) => (
                <option key={c} value={c}>
                  {i + 1}. {t(`categorie.${c}`)}
                </option>
              ))}
            </select>
            {categorie && <p className="text-xs text-gray-500">{t(`categorie.${categorie}_hint`)}</p>}
          </fieldset>

          {/* 3. Onderbouwing */}
          <fieldset className="space-y-4">
            <legend className="mb-1 text-sm font-semibold uppercase tracking-wide text-navy-900">{t('sectie.onderbouwing')}</legend>
            {isMedewerker && boekingen.length > 0 && (
              <div>
                <label htmlFor="klacht-boeking" className={labelCls}>{t('veld.referentieBoeking')}</label>
                <select id="klacht-boeking" className={invoerCls} value={boekingKeuze} onChange={(e) => setBoekingKeuze(e.target.value)}>
                  <option value="">{t('veld.categorieKies')}</option>
                  {boekingen.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.titel} · {datumLang(b.created_at, i18n.language)} · {new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(b.netto_bedrag)}
                    </option>
                  ))}
                  <option value={ANDERS}>{t('veld.referentieAnders')}</option>
                </select>
              </div>
            )}
            {(!isMedewerker || boekingen.length === 0 || boekingKeuze === ANDERS) && (
              <div>
                <label htmlFor="klacht-referentie" className={labelCls}>{t('veld.referentie')}</label>
                <input id="klacht-referentie" className={invoerCls} value={referentie} onChange={(e) => setReferentie(e.target.value)} placeholder={t('veld.referentiePlaceholder')} maxLength={120} required />
              </div>
            )}
            <div>
              <label htmlFor="klacht-omschrijving" className={labelCls}>{t('veld.omschrijving')}</label>
              <textarea id="klacht-omschrijving" className={`${invoerCls} min-h-[160px]`} value={omschrijving} onChange={(e) => setOmschrijving(e.target.value)} placeholder={t('veld.omschrijvingPlaceholder')} maxLength={10000} required />
              <p className="mt-1 flex items-start gap-1.5 text-xs text-amber-700">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                {t('let.privacy')}
              </p>
            </div>
            <div>
              <p className={labelCls}>{t('veld.bijlage')}</p>
              <input ref={bestandRef} type="file" accept="application/pdf,image/png" className="hidden" onChange={kiesBijlage} />
              {bijlage ? (
                <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                  <Paperclip className="h-4 w-4 text-gray-500" aria-hidden="true" />
                  <span className="min-w-0 flex-1 truncate">{bijlage.name}</span>
                  <button type="button" onClick={() => setBijlage(null)} className="inline-flex items-center gap-1 text-gray-500 hover:text-red-600">
                    <X className="h-4 w-4" aria-hidden="true" />
                    {t('veld.bijlageWis')}
                  </button>
                </div>
              ) : (
                <button type="button" onClick={() => bestandRef.current?.click()} className="inline-flex items-center gap-2 rounded-lg border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                  <Paperclip className="h-4 w-4" aria-hidden="true" />
                  {t('veld.bijlageKies')}
                </button>
              )}
              <p className="mt-1 text-xs text-gray-500">{t('veld.bijlageHint')}</p>
            </div>
          </fieldset>

          {/* 4. Gewenste oplossing */}
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-semibold uppercase tracking-wide text-navy-900">{t('sectie.oplossing')}</legend>
            <label htmlFor="klacht-oplossing" className={labelCls}>{t('veld.oplossing')}</label>
            <textarea id="klacht-oplossing" className={`${invoerCls} min-h-[90px]`} value={oplossing} onChange={(e) => setOplossing(e.target.value)} placeholder={t('veld.oplossingPlaceholder')} maxLength={4000} required />
          </fieldset>

          {/* 5. Verklaring */}
          <fieldset className="space-y-4">
            <legend className="mb-1 text-sm font-semibold uppercase tracking-wide text-navy-900">{t('sectie.verklaring')}</legend>
            <label className="flex items-start gap-3 text-sm text-gray-700">
              <input type="checkbox" checked={akkoord} onChange={(e) => setAkkoord(e.target.checked)} className="mt-1" required />
              <span>
                {t('veld.akkoord')}{' '}
                <a href={reglement.pdf} download className="font-medium text-navy-900 underline">PDF</a>
              </span>
            </label>
            {fout && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
                {fout}
              </p>
            )}
            <button
              type="submit"
              disabled={!kanIndienen}
              className="inline-flex items-center gap-2 rounded-xl bg-navy-900 px-6 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-50"
            >
              {indienen.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
              {indienen.isPending ? t('knop.bezig') : t('knop.indienen')}
            </button>
            {formulierAchterKnop && (
              <button
                type="button"
                onClick={() => {
                  setFout(null);
                  setFormulierOpen(false);
                }}
                disabled={indienen.isPending}
                className="ml-3 inline-flex items-center rounded-xl border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {t('knop.annuleren')}
              </button>
            )}
          </fieldset>
        </form>
      )}

      {/* Eigen ingediende klachten */}
      <section>
        <h3 className="mb-3 text-sm font-semibold text-gray-700">{t('mijn.titel')}</h3>
        {(mijn.data ?? []).length === 0 ? (
          <p className="rounded-2xl border border-gray-200 bg-white p-4 text-sm text-gray-500">{t('mijn.leeg')}</p>
        ) : (
          <ul className="space-y-3">
            {(mijn.data ?? []).map((k) => (
              <li key={k.id} className="rounded-2xl border border-gray-200 bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-mono text-sm font-semibold text-gray-900">{k.ticket_nummer}</p>
                    <p className="text-xs text-gray-500">
                      {t(`categorie.${k.categorie}`)} · {t('mijn.ingediend', { datum: datumLang(k.created_at, i18n.language) })}
                    </p>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${STATUS_KLEUR[k.status]}`}>{t(`status.${k.status}`)}</span>
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  {k.status === 'afgehandeld' && k.afgehandeld_op
                    ? t('mijn.afgehandeld', { datum: datumLang(k.afgehandeld_op, i18n.language) })
                    : t('mijn.termijn', { datum: datumLang(k.uiterste_reactiedatum, i18n.language) })}
                </p>
                {k.uitspraak && (
                  <div className="mt-3 rounded-lg border-l-4 border-navy-900 bg-gray-50 px-3 py-2">
                    <p className="text-xs font-semibold text-gray-700">{t('mijn.uitspraak')}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{k.uitspraak}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <KlachtenreglementModal open={modalOpen} onClose={() => setModalOpen(false)} toonFormulierLink={false} variant={variant} />
    </div>
  );
};

export default KlachtenSectie;
